import React, { useCallback, useEffect, useMemo } from "react";
import { Close as CloseIcon, Translate as TranslateIcon } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SITE_BG, SURFACE, TEXT } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import Item from "../../components/Item";
import { usePagesTranslation } from "../../locales";

const primaryTitleSx = { fontWeight: 600, color: "#1976d2" };
const sectionTitleSx = { mb: 2, fontWeight: 600, color: "text.primary" };
const bodySx = { fontSize: "1.1rem", lineHeight: 1.8 };
const normLang = (l) => (l == null ? "" : String(l).trim().toLowerCase());

function TranslateDialogHowToMockup() {
    const { t, lang } = usePagesTranslation();
    const getUiLabel = useCallback(
        (code) => {
            const k = normLang(code);
            if (!k) return "";
            const path = `puzzle.wordMeaning.lang.${k}`;
            const label = t(path);
            if (label === path) return k.charAt(0).toUpperCase() + k.slice(1);
            return label;
        },
        [t]
    );
    const translateButtonLabel = useMemo(
        () => t("puzzle.wordMeaning.translate", { language: getUiLabel(lang) }),
        [t, lang, getUiLabel]
    );

    return (
        <Box component="figure" sx={{ m: 0, mt: 3, maxWidth: 480 }}>
            <Typography
                component="figcaption"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
            >
                {t("howto.translate.where-demo-caption")}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-start" } }}>
                <Paper
                    elevation={4}
                    sx={{
                        maxWidth: 400,
                        width: "100%",
                        borderRadius: 1,
                        overflow: "hidden",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        pointerEvents: "none",
                        userSelect: "none"
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                            alignItems: "center",
                            gap: 1,
                            px: 1.25,
                            py: 1.25,
                            minHeight: 56,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper"
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: "bold",
                                color: "#3BA666",
                                fontSize: "1rem",
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {t("puzzle.wordMeaning.title")}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            tabIndex={-1}
                            startIcon={<TranslateIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                textTransform: "none",
                                borderColor: "#3BA666",
                                color: "#2E8B57",
                                justifySelf: "center"
                            }}
                        >
                            {translateButtonLabel}
                        </Button>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <IconButton size="small" tabIndex={-1} aria-hidden sx={{ color: TEXT.secondary }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={{ p: 2, backgroundColor: SURFACE.sunken }}>
                        <Typography variant="body2" sx={{ m: 0, lineHeight: 1.6, color: "text.primary" }}>
                            {t("howto.translate.demo-placeholder")}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}

export default function HowToTranslate() {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToTranslate" });
    }, []);

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: "left",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ mb: 4, ...primaryTitleSx }}>
                        {t("howto.translate.page-title")}
                    </Typography>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.translate.intro-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.translate.intro-p")}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.translate.where-title")}
                        </Typography>
                        <Typography component="ul" variant="body1" sx={{ ...bodySx, pl: 2, m: 0 }}>
                            <li>
                                <Typography variant="body1" sx={bodySx}>
                                    {t("howto.translate.where-cross")}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1" sx={{ ...bodySx, mt: 1.5 }}>
                                    {t("howto.translate.where-grid")}
                                </Typography>
                            </li>
                        </Typography>
                        <TranslateDialogHowToMockup />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.translate.how-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.translate.how-p")}
                        </Typography>
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
